import { filterPanel } from "../data/panel";
import type { MarketPayload, MarketProduct } from "../types";
import { ApiResponseError, apiUrl, fetchWithTimeout, readApiJson } from "./api";

function isOccupancy(value: unknown): value is MarketProduct["occupancy"] {
  return value === "owner" || value === "investment";
}

function isRepayment(value: unknown): value is MarketProduct["repayment"] {
  return value === "pi" || value === "io";
}

function parseProduct(raw: unknown): MarketProduct | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  if (typeof row.lender !== "string" || typeof row.product !== "string") return null;
  if (typeof row.rate !== "number" || !Number.isFinite(row.rate)) return null;
  if (typeof row.comparisonRate !== "number" || !Number.isFinite(row.comparisonRate)) return null;
  if (!isOccupancy(row.occupancy) || !isRepayment(row.repayment)) return null;
  if (typeof row.maxLvr !== "number" || !Number.isFinite(row.maxLvr)) return null;
  return {
    lender: row.lender,
    product: row.product,
    rate: row.rate,
    comparisonRate: row.comparisonRate,
    occupancy: row.occupancy,
    repayment: row.repayment,
    maxLvr: row.maxLvr,
    minLvr: typeof row.minLvr === "number" ? row.minLvr : undefined,
    minLoan: typeof row.minLoan === "number" ? row.minLoan : undefined,
    cashback: typeof row.cashback === "number" ? row.cashback : 0,
    ongoingFee: typeof row.ongoingFee === "number" ? row.ongoingFee : 0,
    source: row.source === "cdr" ? "cdr" : "snapshot",
    finePrint: typeof row.finePrint === "string" ? row.finePrint : undefined,
    ratesUrl: typeof row.ratesUrl === "string" ? row.ratesUrl : undefined,
    verifiedAt: typeof row.verifiedAt === "string" ? row.verifiedAt : undefined,
    sourceKind:
      row.sourceKind === "cdr" ||
      row.sourceKind === "snapshot" ||
      row.sourceKind === "lender-page" ||
      row.sourceKind === "held"
        ? row.sourceKind
        : undefined,
    reviewStatus: typeof row.reviewStatus === "string" ? row.reviewStatus : undefined,
  };
}

export function parseMarketPayload(raw: unknown): MarketPayload | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  if (!Array.isArray(row.products) || typeof row.asAt !== "string") return null;
  const products = filterPanel(row.products.map(parseProduct).filter((p): p is MarketProduct => p != null));
  if (!products.length) return null;
  const source = row.source === "cdr" || row.source === "mixed" || row.source === "snapshot" ? row.source : "snapshot";
  return {
    asAt: row.asAt,
    asAtIso: typeof row.asAtIso === "string" ? row.asAtIso : "",
    source,
    brands: typeof row.brands === "number" ? row.brands : new Set(products.map((p) => p.lender)).size,
    products,
    note: typeof row.note === "string" ? row.note : "",
  };
}

export async function fetchLiveMarket(
  deps: { fetch?: typeof fetch; url?: string; timeoutMs?: number } = {},
): Promise<MarketPayload | null> {
  const fetchImpl = deps.fetch ?? fetch;
  const url = deps.url ?? apiUrl("/rates");
  try {
    const res = await fetchWithTimeout(
      url,
      { headers: { Accept: "application/json" } },
      deps.timeoutMs ?? 12_000,
      fetchImpl,
    );
    if (!res.ok) return null;
    const body = await readApiJson<unknown>(res);
    return parseMarketPayload(body);
  } catch (error) {
    if (error instanceof ApiResponseError) return null;
    return null;
  }
}
